/**
 * Customer signing helpers (Phase 12, shared with Phase 13 quote signing).
 *
 * - `canCustomerSign` is the ONE signing gate (D-01). Every signing surface
 *   reads eligibility through it: the server-owned `customerCanSign` ANDed
 *   with the tenant rollout flag, decided in this one function, failing closed
 *   for anything other than a literal `true` on both.
 * - `buildSignPayload` is the ONLY way a sign request body is built (D-04).
 *   It allowlists `signature_image` (+ trimmed `notes`) into a fresh object
 *   and never spreads its input, so admin-only params (force_accept,
 *   override_reason, source) can never reach the API.
 * - Every [ASSUMED] API value lives in exactly one place (D-06): the body key
 *   below, the endpoint in `api/endpoints.ts`, the wire key in the invoice
 *   adapter. Phase 14 swaps each in one line once the API confirms them.
 */

/** Pad ink state reported by `SignaturePad` (`update:inkState`). */
export type SignatureInkState = 'empty' | 'insufficient' | 'ready';

/** Classified sign failure kinds rendered by the sign dialog error band. */
export type SignErrorKind =
  | 'ineligible'
  | 'stale-schedule'
  | 'unavailable'
  | 'network';

/** The allowlisted sign request body. */
export interface SignPayload {
  /** [ASSUMED] TC-718 body key for the drawn PNG data URL. */
  signature_image: string;
  /** Quote-only (Phase 13). Present only when non-empty after trim. */
  notes?: string;
}

/** Request timeout for the sign POST (UI-SPEC; tune in Phase 14). */
export const SIGN_REQUEST_TIMEOUT_MS = 30_000;

const PNG_DATA_URL = /^data:image\/png;base64,[A-Za-z0-9+/=]+$/;

/**
 * Allowlist builder (D-04). Throws when `signatureImage` is not a PNG base64
 * data URL (SVG, JPEG, empty, or unprefixed values are rejected).
 */
export function buildSignPayload(input: {
  signatureImage: string;
  notes?: string;
}): SignPayload {
  if (!PNG_DATA_URL.test(input.signatureImage)) {
    throw new Error('signature must be a PNG data URL');
  }
  // [ASSUMED] TC-718: body key `signature_image`. Phase 14 swaps here only (D-06).
  const body: SignPayload = { signature_image: input.signatureImage };
  const notes = input.notes?.trim();
  if (notes) body.notes = notes;
  return body;
}

/**
 * D-01 single signing gate, fail closed: signing is enabled only when the
 * tenant rollout flag is a literal `true` AND `customerCanSign` is a literal
 * `true`.
 *
 * The caller passes the tenant `customer_portal_signing` rollout flag (read via
 * `useTenantStore().isFeatureEnabled`) as `signingEnabled`, so this util stays
 * pure and store-free. The server already ANDs the same Unleash flag into
 * `customer_can_sign` and owns eligibility (TC-684 AC1); this client AND is
 * defense in depth, so no tenant sees a signing surface before its rollout
 * flag is on (TC-684 AC9, TC-720).
 */
export function canCustomerSign(
  entity: { customerCanSign?: unknown } | null | undefined,
  options: { signingEnabled: boolean },
): boolean {
  return options.signingEnabled === true && entity?.customerCanSign === true;
}
